import {
  getFollowStatusFn,
  getLibraryProgressFn,
} from "@/actions/community/handler";
import { getLibraryPathOptions } from "@/actions/library/handler";
import { LibraryFollowButton } from "@/components/library/library-follow-button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { libraryDifficultyLabels } from "@/utils/library-catalog";
import { seo } from "@/utils/seo";
import { useQuery } from "@tanstack/react-query";
import {
  Link,
  createFileRoute,
  notFound,
  useRouteContext,
} from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, Check, Clock } from "lucide-react";

export const Route = createFileRoute("/_app/kutuphane/$pathSlug")({
  loader: async ({ context, params }) => {
    const data = await context.queryClient.ensureQueryData(
      getLibraryPathOptions(params.pathSlug),
    );
    if (!data.path) throw notFound();
    return data.path;
  },
  head: ({ loaderData }) => ({
    meta: seo({
      title: loaderData
        ? `${loaderData.title} | Egzersiz Bilimi Kütüphanesi`
        : "Öğrenme Yolu | AtalayTheCoach",
      description: loaderData?.description || "Egzersiz bilimi öğrenme yolu.",
      canonical: loaderData
        ? `https://atalaythecoach.com/kutuphane/${loaderData.slug}`
        : "https://atalaythecoach.com/kutuphane",
    }),
  }),
  component: LibraryPathPage,
});

function LibraryPathPage() {
  const path = Route.useLoaderData();
  const { user } = useRouteContext({ from: "/_app" });
  const progress = useQuery({
    queryKey: ["library-progress"],
    queryFn: () => getLibraryProgressFn(),
    enabled: Boolean(user),
  });
  const following = useQuery({
    queryKey: ["content-follow", "library_path", path.pathId],
    queryFn: () =>
      getFollowStatusFn({
        data: { resourceType: "library_path", resourceId: path.pathId },
      }),
    enabled: Boolean(user),
  });
  const completed = new Set(progress.data?.completedEntryIds || []);
  const completedCount = path.entries.filter((entry) =>
    completed.has(entry.entryId),
  ).length;
  const percent = path.entries.length
    ? (completedCount / path.entries.length) * 100
    : 0;

  return (
    <main className="min-h-screen bg-background pt-28 pb-20 text-foreground">
      <div className="container mx-auto max-w-5xl px-4">
        <Link
          to="/kutuphane"
          className="inline-flex items-center text-muted-foreground text-sm hover:text-foreground"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Kütüphaneye dön
        </Link>

        <header className="mt-8 grid gap-8 md:grid-cols-[1fr_auto] md:items-end">
          <div>
            <Badge variant="outline">Kronolojik öğrenme yolu</Badge>
            <h1 className="mt-4 font-extrabold text-4xl tracking-tight sm:text-5xl">
              {path.title}
            </h1>
            <p className="mt-4 max-w-3xl text-lg text-muted-foreground leading-8">
              {path.description}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {path.topics.map((topic) => (
                <Badge key={topic} variant="secondary">
                  {topic}
                </Badge>
              ))}
            </div>
          </div>
          <LibraryFollowButton
            pathId={path.pathId}
            initialFollowing={following.data?.following || false}
            authenticated={Boolean(user)}
          />
        </header>

        <section className="mt-10 rounded-2xl border bg-muted/20 p-5">
          <div className="flex items-center justify-between gap-4 text-sm">
            <strong>İlerlemeniz</strong>
            <span className="text-muted-foreground">
              {completedCount}/{path.entries.length} kaynak
            </span>
          </div>
          <Progress value={percent} className="mt-3" />
        </section>

        <div className="relative mt-12 space-y-5 before:absolute before:top-6 before:bottom-6 before:left-5 before:w-px before:bg-border sm:before:left-7">
          {path.entries.map((entry, index) => {
            const isCompleted = completed.has(entry.entryId);
            return (
              <Card key={entry.entryId} className="relative ml-12 sm:ml-16">
                <div className="-left-12 sm:-left-16 absolute top-5 flex h-10 w-10 items-center justify-center rounded-full border bg-background font-bold text-sm sm:h-14 sm:w-14">
                  {isCompleted ? (
                    <Check className="h-5 w-5 text-emerald-500" />
                  ) : (
                    index + 1
                  )}
                </div>
                <CardContent className="p-5 sm:p-6">
                  <div className="flex flex-wrap items-center gap-2 text-muted-foreground text-xs">
                    <Badge variant="secondary">
                      {entry.publicationDate.slice(0, 4)}
                    </Badge>
                    <span>{entry.journal}</span>
                    <span className="inline-flex items-center">
                      <Clock className="mr-1 h-3 w-3" />
                      {libraryDifficultyLabels[entry.difficulty]}
                    </span>
                  </div>
                  <h2 className="mt-3 font-bold text-xl sm:text-2xl">
                    {entry.turkishTitle}
                  </h2>
                  <p className="mt-3 line-clamp-3 text-muted-foreground leading-7">
                    {entry.summary}
                  </p>
                  {entry.chronologyContext && (
                    <p className="mt-3 border-primary/40 border-l-2 pl-3 text-sm leading-6">
                      <strong>Bu kaynak neden burada?</strong>{" "}
                      {entry.chronologyContext}
                    </p>
                  )}
                  <Link
                    to="/kutuphane/kaynak/$entrySlug"
                    params={{ entrySlug: entry.slug }}
                    className="mt-5 inline-flex items-center font-semibold text-primary"
                  >
                    Açıklamalı okumaya geç{" "}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </main>
  );
}
